import type { Document } from '@/store/parser'

export type DeckEvent = {
  id: string
  names: Partial<Record<string, string>>
  lastPublished: Date
}

export const EVENT_RETENTION_MS = 30 * 24 * 60 * 60 * 1000

// Un id d'événement est toujours 3 lettres suivies de 2 chiffres (ex. PMU11) —
// utilisé par useIdentifierMatcher (omnibox) et le tool IA search_events.
export const EVENT_ID_RE = /^[A-Za-z]{3}\d{2}$/

const EVENT_QUERY_RE = /\bevent:"?afpevent:([A-Za-z0-9]+)"?/gi

// Les ids d'événement AFP ne sont pas garantis casse-stable d'une dépêche à
// l'autre côté API — sans normalisation, la même actualité peut apparaître
// comme deux events distincts ("RUG01" / "rug01") dans les dédoublonnages.
export function normalizeEventId(id: string): string {
  return id.trim().toUpperCase()
}

export function eventIdsFromQuery(query: string | undefined | null): string[] {
  if (!query) return []
  return [...new Set([...query.matchAll(EVENT_QUERY_RE)].map(m => m[1]!))]
}

export function mergeEvents(
  existing: (DeckEvent | undefined)[],
  docs: Pick<Document, 'lang' | 'published' | 'events'>[]
): DeckEvent[] {
  const merged = new Map<string, DeckEvent>()
  for (const ev of existing) {
    if (!ev) continue
    // Auto-guérit les ids en casse hétérogène déjà persistés avant ce fix.
    const id = normalizeEventId(ev.id)
    merged.set(id, { ...ev, id })
  }

  for (const doc of docs) {
    const published = new Date(doc.published)
    for (const { id: rawId, name } of doc.events ?? []) {
      if (!rawId) continue
      const id = normalizeEventId(rawId)
      const current = merged.get(id)
      merged.set(id, {
        ...current,
        id,
        names: name
          ? { ...current?.names, [doc.lang]: name }
          : { ...current?.names },
        lastPublished:
          current && current.lastPublished > published
            ? current.lastPublished
            : published
      })
    }
  }
  return [...merged.values()]
}

export function isEventExpired(
  event: DeckEvent,
  referencedIds: Set<string>,
  now = Date.now()
): boolean {
  if (referencedIds.has(event.id)) return false
  return now - new Date(event.lastPublished).getTime() > EVENT_RETENTION_MS
}

export function pickEventName(
  event: DeckEvent | undefined,
  langs: (string | undefined)[]
): string | undefined {
  if (!event) return undefined
  for (const lang of langs) {
    const name = lang && event.names[lang]
    if (name) return name
  }
  return Object.values(event.names).find(Boolean)
}

// Type de retour partagé par les recherches d'événements du store (getters.ts).
export type EventMatch = { id: string; name: string }
